import { useRef } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProductType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import SpinnerBlock from 'components/others/SpinnerBlock';
import TableHeadTdOrder from 'components/others/TableHeadTdOrder';
import OffProductCard from './OffProductCard';
import OffProductPrice from './OffProductPrice';
import ProductTableBody from './ProductTableBody';
import { useProduct } from './context/ProductProvider';

interface ProductsTableProps {
    productsList: ProductType[];
    fetchProducts: (force?: boolean) => Promise<unknown>;
}

const ProductsTable = ({ productsList, fetchProducts }: ProductsTableProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Products']);
    const { showSpinner, productsOrder, productsDesc, setProductsOrder, setProductsDesc, productCheck, setProductCheck } =
        useProduct();

    const refCheckAll = useRef<HTMLInputElement>(null);
    const allProductsIdOnPage = productsList?.map((p) => p.ID) || [];

    const handleOrderClick = (value: string, desc: boolean) => {
        setProductsOrder(value);
        setProductsDesc(desc);
    };

    return (
        <>
            <OffProductCard fetchProducts={fetchProducts} />

            <OffProductPrice fetchProducts={fetchProducts} />

            <SpinnerBlock onlyBlur={true} show={showSpinner} />
            <div className="crm-table-wrap">
                <Table className="ProductTable table-hover">
                    <thead>
                        <tr className="bg-light">
                            <td className="head-check">
                                <input
                                    className="form-check-input me-2"
                                    ref={refCheckAll}
                                    type="checkbox"
                                    disabled={!hasUserAccess(auth?.user, 'prodEdit')}
                                    checked={allProductsIdOnPage.every((id) => productCheck.includes(id))}
                                    onChange={(e) => {
                                        setProductCheck((prev) => {
                                            if (e.target.checked) {
                                                const union = Array.from(new Set([...prev, ...allProductsIdOnPage]));
                                                return [...union];
                                            }
                                            return prev.filter((p) => !allProductsIdOnPage.includes(p));
                                        });
                                    }}
                                />
                            </td>
                            <td className="head-name">
                                <TableHeadTdOrder
                                    title={t('fields.name')}
                                    onClick={handleOrderClick}
                                    value={productsOrder}
                                    desc={productsDesc}
                                    fieldName="prod_name"
                                />
                            </td>

                            <td className="head-vendor">
                                <TableHeadTdOrder
                                    title={t('fields.category')}
                                    onClick={handleOrderClick}
                                    value={productsOrder}
                                    desc={productsDesc}
                                    fieldName="category_name"
                                />
                            </td>

                            <td className="head-id">
                                <TableHeadTdOrder
                                    title={t('fields.id')}
                                    onClick={handleOrderClick}
                                    value={productsOrder}
                                    desc={productsDesc}
                                    fieldName="ID"
                                />
                            </td>

                            <td className="head-article">
                                <TableHeadTdOrder
                                    title={t('fields.article')}
                                    onClick={handleOrderClick}
                                    value={productsOrder}
                                    desc={productsDesc}
                                    fieldName="prod_article"
                                />
                            </td>

                            <td className="head-price">{t('fields.priceList')}</td>

                            <td className="head-updated">
                                <TableHeadTdOrder
                                    title={t('fields.updated')}
                                    onClick={handleOrderClick}
                                    value={productsOrder}
                                    desc={productsDesc}
                                    fieldName="prod_updated"
                                />
                            </td>

                            {hasUserAccess(auth?.user, 'prodEdit') && <td className="head-action">{t('fields.actions')}</td>}
                        </tr>
                    </thead>

                    <ProductTableBody productsList={productsList} fetchProducts={fetchProducts} />
                </Table>
            </div>
        </>
    );
};

export default ProductsTable;
